import { useEffect, useMemo, useState, type FC } from 'react';
import { StyleSheet, View } from 'react-native';
import { Slider } from 'react-native-awesome-slider';
import { Text } from 'react-native-paper';
import {
  runOnJS,
  useAnimatedReaction,
  useSharedValue
} from 'react-native-reanimated';

import { MIN_RANGE } from '@/features/feature-management/types/geoAreaSelectionFeature';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      minHeight: RANGE_SLIDER_HEIGHT,
      justifyContent: 'center'
    },
    slider: {
      width: '100%',
      height: 16,
      borderRadius: 16,
      overflow: 'hidden'
    },
    thumbContainer: {
      gap: 12,
      borderRadius: 4,
      alignItems: 'center',
      backgroundColor: theme.colors.surfaceContainerLow,
      marginTop: -32
    },
    hint: {
      borderRadius: 4,
      paddingVertical: 4,
      paddingHorizontal: 8,
      marginHorizontal: -20,
      backgroundColor: theme.colors.primary,
      color: theme.colors.surface
    },
    thumb: {
      width: 4,
      borderRadius: 8,
      marginHorizontal: 8,
      backgroundColor: theme.colors.primary,
      height: 32
    }
  });

/**
 * Height reserved for the slider (thumb label + track). Layouts embedding the
 * slider must add this to their map container so the thumb is not clipped.
 */
export const RANGE_SLIDER_HEIGHT = 76;

type Props = {
  initialRange: number;
  maxRange: number;
  minRange?: number;
  onRangeChange: (range: number) => void;
};

export const RangeSlider: FC<Props> = ({
  initialRange,
  maxRange,
  minRange = MIN_RANGE,
  onRangeChange
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const range = useSharedValue(initialRange);
  const min = useSharedValue(minRange);
  const max = useSharedValue(maxRange);

  useEffect(() => range.set(initialRange), [initialRange, range]);
  useEffect(() => min.set(minRange), [minRange, min]);

  // Belt-and-braces: react-native-awesome-slider can briefly overshoot bounds on
  // fast flicks, leaving the shared value negative (or above max) for a frame.
  // Snap it back inside [min, max] so the thumb label and any onValueChange
  // callback always see a valid value.
  useAnimatedReaction(
    () => range.get(),
    (current) => {
      'worklet';
      const lo = min.get();
      const hi = max.get();
      if (current < lo) range.set(lo);
      else if (current > hi) range.set(hi);
    }
  );

  const clampForLabel = (value: number): number =>
    Math.min(Math.max(value, minRange), maxRange);

  const formatLabel = (value: number): string =>
    `${(Math.ceil(clampForLabel(value) / 500) * 0.5).toString().replaceAll('.', ',')} km`;

  // Keep the label in local React state so it updates whenever the shared
  // value changes — including after the sync effect runs on `initialRange`
  // changes (where reading `range.get()` directly in render would miss the
  // update because shared-value writes don't trigger React re-renders).
  const [label, setLabel] = useState(() => formatLabel(initialRange));

  useAnimatedReaction(
    () => range.get(),
    (current) => {
      'worklet';
      const clamped = Math.min(Math.max(current, minRange), maxRange);
      const next = `${(Math.ceil(clamped / 500) * 0.5).toString().replaceAll('.', ',')} km`;
      runOnJS(setLabel)(next);
    }
  );

  return (
    <View style={styles.container}>
      <Slider
        progress={range}
        minimumValue={min}
        maximumValue={max}
        renderBubble={() => <></>}
        renderThumb={() => (
          <View style={styles.thumbContainer}>
            <Text style={styles.hint} variant="bodySmall">
              {label}
            </Text>
            <View style={styles.thumb} />
          </View>
        )}
        style={{ height: 16 }}
        containerStyle={styles.slider}
        theme={{
          minimumTrackTintColor: theme.colors.primary,
          maximumTrackTintColor: theme.colors.secondaryContainer
        }}
        onValueChange={onRangeChange}
      />
    </View>
  );
};
